import React, { useEffect, useState } from "react";
import {
	ArrowPathIcon,
	PencilIcon,
	PlusIcon,
} from "@heroicons/react/24/outline";
import { useSeatmapStore } from "../../../store/seatmapStore";

const defaultForm = {
	code: "",
	name: "",
	description: "",
	category: "train",
	is_active: true,
};

export default function SeatmapTrainTypes() {
	const {
		trainTypes,
		isLoading,
		error,
		fetchTrainTypes,
		createTrainType,
		updateTrainType,
		clearError,
	} = useSeatmapStore();

	const [showInactive, setShowInactive] = useState(false);
	const [isModalOpen, setIsModalOpen] = useState(false);
	const [editingType, setEditingType] = useState(null);
	const [formData, setFormData] = useState(defaultForm);

	useEffect(() => {
		fetchTrainTypes(showInactive);
	}, [showInactive, fetchTrainTypes]);

	const openModal = (trainType = null) => {
		if (trainType) {
			setEditingType(trainType);
			setFormData({
				code: trainType.code || "",
				name: trainType.name || "",
				description: trainType.description || "",
				category: trainType.category || "train",
				is_active: trainType.is_active !== false,
			});
		} else {
			setEditingType(null);
			setFormData(defaultForm);
		}
		setIsModalOpen(true);
	};

	const closeModal = () => {
		setIsModalOpen(false);
		setEditingType(null);
		setFormData(defaultForm);
		clearError();
	};

	const handleSubmit = async (event) => {
		event.preventDefault();
		try {
			if (editingType) {
				await updateTrainType(editingType.code, {
					name: formData.name,
					description: formData.description,
					category: formData.category,
					is_active: formData.is_active,
				});
			} else {
				await createTrainType(formData);
			}
			closeModal();
		} catch (err) {
			alert(err.message || "열차종 저장에 실패했습니다.");
		}
	};

	return (
		<div className="py-6">
			<div className="flex flex-wrap items-center justify-between gap-4 mb-6">
				<h1 className="text-2xl font-bold text-gray-900">
					좌석배치도 열차종 관리
				</h1>
				<div className="flex items-center gap-4">
					<label className="flex items-center gap-2 text-sm text-gray-600">
						<input
							type="checkbox"
							checked={showInactive}
							onChange={(event) => setShowInactive(event.target.checked)}
							className="rounded border-gray-300"
						/>
						비활성 항목 표시
					</label>
					<button
						onClick={() => openModal()}
						className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
					>
						<PlusIcon className="w-5 h-5" />
						열차종 추가
					</button>
				</div>
			</div>

			{error && (
				<div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
					{error}
					<button onClick={clearError} className="ml-2 underline">
						닫기
					</button>
				</div>
			)}

			<div className="bg-white shadow rounded-lg overflow-hidden">
				{isLoading ? (
					<div className="flex items-center justify-center py-12">
						<ArrowPathIcon className="w-8 h-8 text-gray-400 animate-spin" />
						<span className="ml-2 text-gray-500">로딩 중...</span>
					</div>
				) : trainTypes.length === 0 ? (
					<div className="text-center py-12 text-gray-500">
						등록된 열차종이 없습니다.
					</div>
				) : (
					<table className="min-w-full divide-y divide-gray-200">
						<thead className="bg-gray-50">
							<tr>
								<th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
									코드
								</th>
								<th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
									이름
								</th>
								<th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
									구분
								</th>
								<th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
									설명
								</th>
								<th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
									상태
								</th>
								<th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
									작업
								</th>
							</tr>
						</thead>
						<tbody className="bg-white divide-y divide-gray-200">
							{trainTypes.map((item) => (
								<tr
									key={item.code}
									className={item.is_active === false ? "bg-gray-50" : ""}
								>
									<td className="px-6 py-4 whitespace-nowrap text-sm font-mono text-gray-600">
										{item.code}
									</td>
									<td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
										{item.name}
									</td>
									<td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
										{item.category === "bus" ? "버스" : "열차"}
									</td>
									<td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
										{item.description || "-"}
									</td>
									<td className="px-6 py-4 whitespace-nowrap">
										<span
											className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
												item.is_active === false
													? "bg-gray-200 text-gray-600"
													: "bg-green-100 text-green-800"
											}`}
										>
											{item.is_active === false ? "비활성" : "활성"}
										</span>
									</td>
									<td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
										<button
											onClick={() => openModal(item)}
											className="text-blue-600 hover:text-blue-900"
											title="수정"
										>
											<PencilIcon className="w-5 h-5" />
										</button>
									</td>
								</tr>
							))}
						</tbody>
					</table>
				)}
			</div>

			{isModalOpen && (
				<div className="fixed inset-0 z-50 overflow-y-auto">
					<div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
						<div
							className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75"
							onClick={closeModal}
						/>

						<div className="inline-block w-full max-w-md p-6 my-8 overflow-hidden text-left align-middle transition-all transform bg-white shadow-xl rounded-lg">
							<h3 className="text-lg font-medium leading-6 text-gray-900 mb-4">
								{editingType ? "열차종 수정" : "열차종 추가"}
							</h3>

							<form onSubmit={handleSubmit}>
								<div className="mb-4">
									<label className="block text-sm font-medium text-gray-700 mb-1">
										코드 <span className="text-red-500">*</span>
									</label>
									<input
										type="text"
										value={formData.code}
										onChange={(event) =>
											setFormData({ ...formData, code: event.target.value })
										}
										className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
										placeholder="예: KTX"
										required
										disabled={!!editingType}
									/>
									{editingType && (
										<p className="text-xs text-gray-500 mt-1">
											코드는 수정할 수 없습니다.
										</p>
									)}
								</div>

								<div className="mb-4">
									<label className="block text-sm font-medium text-gray-700 mb-1">
										이름 <span className="text-red-500">*</span>
									</label>
									<input
										type="text"
										value={formData.name}
										onChange={(event) =>
											setFormData({ ...formData, name: event.target.value })
										}
										className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
										required
									/>
								</div>

								<div className="mb-4">
									<label className="block text-sm font-medium text-gray-700 mb-1">
										구분 <span className="text-red-500">*</span>
									</label>
									<select
										value={formData.category}
										onChange={(event) =>
											setFormData({ ...formData, category: event.target.value })
										}
										className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
										required
									>
										<option value="train">열차</option>
										<option value="bus">버스</option>
									</select>
								</div>

								<div className="mb-4">
									<label className="block text-sm font-medium text-gray-700 mb-1">
										설명
									</label>
									<textarea
										value={formData.description}
										onChange={(event) =>
											setFormData({ ...formData, description: event.target.value })
										}
										className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
										rows={3}
									/>
								</div>

								<div className="mb-4 flex items-center gap-2">
									<input
										type="checkbox"
										checked={formData.is_active}
										onChange={(event) =>
											setFormData({ ...formData, is_active: event.target.checked })
										}
										className="rounded border-gray-300"
									/>
									<span className="text-sm text-gray-700">
										활성 상태로 사용
									</span>
								</div>

								<div className="flex justify-end gap-2 mt-6">
									<button
										type="button"
										onClick={closeModal}
										className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
									>
										취소
									</button>
									<button
										type="submit"
										disabled={isLoading}
										className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
									>
										{isLoading ? "처리 중..." : editingType ? "수정" : "추가"}
									</button>
								</div>
							</form>
						</div>
					</div>
				</div>
			)}
		</div>
	);
}
